import React, { useState } from 'react';
import { useMoveBoxesBulk } from '../hooks/useBoxesBulk';
import { usePieces } from '../hooks/usePieces';
import { useZones } from '../hooks/useZones';
import { useToastContext } from '../contexts/ToastContext';

interface BulkMoveBoxesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: string[];
}

const BulkMoveBoxesModal: React.FC<BulkMoveBoxesModalProps> = ({
  isOpen,
  onClose,
  selectedIds,
}) => {
  const { showSuccess, showError, showWarning, showInfo } = useToastContext();
  const moveBoxesBulkMutation = useMoveBoxesBulk();
  const { data: pieces = [] } = usePieces();
  const { data: zones = [] } = useZones();

  const [locationType, setLocationType] = useState<'piece' | 'zone'>('piece');
  const [selectedPiece, setSelectedPiece] = useState('');
  const [selectedZone, setSelectedZone] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await moveBoxesBulkMutation.mutateAsync({
        boxIds: selectedIds,
        pieceId: locationType === 'piece' ? selectedPiece : undefined,
        zoneId: locationType === 'zone' ? selectedZone : undefined,
      });
      showSuccess('Boxes déplacées', `${selectedIds.length} box(es) déplacée(s) avec succès.`);
      onClose();
    } catch (error: any) {
      console.error('Erreur:', error);
      showError('Erreur', error.response?.data?.message || error.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  🚚 Déplacer {selectedIds.length} box(es)
                </h3>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de destination
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setLocationType('piece')}
                      className={`p-3 border-2 rounded-lg transition-all ${
                        locationType === 'piece'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="text-2xl mb-1">🚪</div>
                      <div className="text-sm font-medium">Pièce</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setLocationType('zone')}
                      className={`p-3 border-2 rounded-lg transition-all ${
                        locationType === 'zone'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="text-2xl mb-1">🏠</div>
                      <div className="text-sm font-medium">Zone</div>
                    </button>
                  </div>
                </div>

                {locationType === 'piece' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pièce de destination
                    </label>
                    <select
                      value={selectedPiece}
                      onChange={(e) => setSelectedPiece(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    >
                      <option value="">Sélectionner une pièce</option>
                      {pieces.map((piece: any) => (
                        <option key={piece.id} value={piece.id}>
                          {piece.nom} ({piece.code})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {locationType === 'zone' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zone de destination
                    </label>
                    <select
                      value={selectedZone}
                      onChange={(e) => setSelectedZone(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    >
                      <option value="">Sélectionner une zone</option>
                      {zones.map((zone: any) => (
                        <option key={zone.id} value={zone.id}>
                          {zone.nom} ({zone.code})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Les {selectedIds.length} boxes seront déplacées vers le même emplacement.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={moveBoxesBulkMutation.isPending}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {moveBoxesBulkMutation.isPending ? 'Déplacement...' : 'Déplacer'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkMoveBoxesModal;

